'use client';

import { useEffect, useState, useCallback } from 'react';
import {
  CONSENT_COOKIE_NAME,
  CONSENT_COOKIE_MAX_AGE,
  CONSENT_VERSION,
  DEFAULT_CONSENT,
  parseConsent,
  serializeConsent,
  hasConsent,
  type ConsentState,
  type ConsentCategory,
} from './consent';

const CONSENT_CHANGE_EVENT = 'nqoro:consentchange';

function readCookie(): ConsentState {
  if (typeof document === 'undefined') return DEFAULT_CONSENT;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE_NAME}=([^;]*)`));
  if (!match || !match[1]) return DEFAULT_CONSENT;
  try {
    return parseConsent(decodeURIComponent(match[1]));
  } catch {
    return DEFAULT_CONSENT;
  }
}

function writeCookie(state: ConsentState) {
  if (typeof document === 'undefined') return;
  const value = encodeURIComponent(serializeConsent(state));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${CONSENT_COOKIE_NAME}=${value}` +
    `; Max-Age=${CONSENT_COOKIE_MAX_AGE}` +
    `; Path=/` +
    `; SameSite=Lax` +
    secure;
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: state }));
}

export function getClientConsent(): ConsentState {
  return readCookie();
}

/** Persiste decisión del usuario y emite evento global. */
export function setConsent(input: {
  preferences?: boolean;
  analytics?: boolean;
  marketing?: boolean;
}): ConsentState {
  const next: ConsentState = {
    version: CONSENT_VERSION,
    necessary: true,
    preferences: input.preferences === true,
    analytics: input.analytics === true,
    marketing: input.marketing === true,
    decidedAt: new Date().toISOString(),
  };
  writeCookie(next);
  return next;
}

/** Helpers de un solo click. */
export function acceptAllConsent(): ConsentState {
  return setConsent({ preferences: true, analytics: true, marketing: true });
}

export function rejectAllConsent(): ConsentState {
  return setConsent({ preferences: false, analytics: false, marketing: false });
}

/**
 * Hook reactivo: re-renderiza cuando se emite `nqoro:consentchange`.
 * Inicializa con la cookie actual.
 */
export function useConsent(): {
  consent: ConsentState;
  hasDecided: boolean;
  has: (category: ConsentCategory) => boolean;
  set: typeof setConsent;
  acceptAll: () => ConsentState;
  rejectAll: () => ConsentState;
} {
  const [consent, setLocalState] = useState<ConsentState>(DEFAULT_CONSENT);

  useEffect(() => {
    setLocalState(readCookie());
    function onChange(e: Event) {
      const detail = (e as CustomEvent<ConsentState>).detail;
      if (detail) setLocalState(detail);
    }
    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
  }, []);

  const has = useCallback((category: ConsentCategory) => hasConsent(consent, category), [consent]);

  return {
    consent,
    hasDecided: consent.decidedAt !== null,
    has,
    set: setConsent,
    acceptAll: acceptAllConsent,
    rejectAll: rejectAllConsent,
  };
}
